import { useEffect, useState } from "react";
import AdminLayout, { KPICard } from "../../components/admin/AdminLayout";
import { analyticsSummary, analyticsFunnel, analyticsSources, analyticsDaily, analyticsCampaigns } from "../../lib/admin-api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Eye, Users, Target, Phone, MessageCircle, FileDown, TrendingUp, TrendingDown } from "lucide-react";

const PERIODS = [
    { value: 7, label: "7d" },
    { value: 14, label: "14d" },
    { value: 30, label: "30d" },
    { value: 60, label: "60d" },
    { value: 90, label: "90d" },
];
const SITIOS = [
    { value: "", label: "Todos" },
    { value: "eclipse", label: "Eclipse" },
    { value: "flexshade", label: "FlexShade" },
    { value: "velarias", label: "Velarias" },
];

const CHANNEL_LABELS = {
    google_ads: "Google Ads",
    meta_ads: "Meta Ads",
    organico: "Orgánico",
    directo: "Directo",
    whatsapp: "WhatsApp",
    otros: "Otros",
};

export default function AdminAnalytics() {
    const [days, setDays] = useState(30);
    const [sitio, setSitio] = useState("");
    const [summary, setSummary] = useState(null);
    const [funnel, setFunnel] = useState([]);
    const [sources, setSources] = useState([]);
    const [daily, setDaily] = useState([]);
    const [camp, setCamp] = useState({ top_campaigns: [], top_keywords: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const s = sitio || undefined;
                const [a, b, c, d, e] = await Promise.all([
                    analyticsSummary(days, s),
                    analyticsFunnel(days, s),
                    analyticsSources(days, s),
                    analyticsDaily(days, s),
                    analyticsCampaigns(days),
                ]);
                if (!alive) return;
                setSummary(a);
                setFunnel(b.stages || []);
                setSources(c.items || []);
                setDaily(d.items || []);
                setCamp(e || { top_campaigns: [], top_keywords: [] });
            } catch (err) {
                console.error(err);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [days, sitio]);

    const maxStage = Math.max(...funnel.map((s) => s.count), 1);

    return (
        <AdminLayout title="Analytics" subtitle="Visitas, conversiones, fuentes y campañas (anti-adblocker)" testid="admin-analytics-page" actions={
            <div className="flex gap-2">
                <select value={sitio} onChange={(e) => setSitio(e.target.value)} className="h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white" data-testid="an-sitio">
                    {SITIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <div className="flex gap-0.5 bg-neutral-100 p-0.5 rounded" data-testid="an-period">
                    {PERIODS.map((p) => (
                        <button key={p.value} onClick={() => setDays(p.value)} className={`px-3 py-1.5 text-xs rounded ${days === p.value ? "bg-white shadow-sm font-semibold" : "text-neutral-500"}`} data-testid={`an-period-${p.value}`}>{p.label}</button>
                    ))}
                </div>
            </div>
        }>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
                <KPICardDelta label="Visitas" value={summary?.visitas} delta={summary?.delta_pct?.visitas} icon={Eye} />
                <KPICardDelta label="Únicos" value={summary?.unicos} icon={Users} />
                <KPICardDelta label="Leads" value={summary?.leads} delta={summary?.delta_pct?.leads} icon={Target} accent="text-eclipse-cta" />
                <KPICardDelta label="CVR" value={summary ? `${summary.cvr}%` : "—"} delta={summary?.delta_pct?.cvr} unit="pp" />
                <KPICardDelta label="Catálogo" value={summary?.catalogos} icon={FileDown} />
                <KPICardDelta label="WhatsApp" value={summary?.whatsapp_clicks} delta={summary?.delta_pct?.whatsapp_clicks} icon={MessageCircle} accent="text-green-600" />
                <KPICardDelta label="Teléfono" value={summary?.phone_clicks} delta={summary?.delta_pct?.phone_clicks} icon={Phone} accent="text-blue-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-md p-5 lg:col-span-2">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Visitas vs Leads (diario)</div>
                    <div style={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="visitas" stroke="#1A1A2E" strokeWidth={2} dot={false} name="Visitas" />
                                <Line type="monotone" dataKey="leads" stroke="#E86A1F" strokeWidth={2} dot={false} name="Leads" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-md p-5">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Embudo de conversión</div>
                    <div className="space-y-2.5" data-testid="an-funnel">
                        {funnel.map((s, i) => {
                            const pct = (s.count / maxStage) * 100;
                            return (
                                <div key={s.key} className="space-y-1">
                                    <div className="flex justify-between text-xs"><span className="text-neutral-700 font-medium">{i + 1}. {s.label}</span><span className="text-neutral-900 font-bold">{s.count}</span></div>
                                    <div className="h-6 bg-neutral-100 rounded overflow-hidden relative">
                                        <div className="h-full bg-eclipse-cta transition-all" style={{ width: `${pct}%` }}></div>
                                        {i > 0 && s.drop_pct > 0 && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-red-600 font-mono">-{s.drop_pct}%</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-md p-5">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Por canal</div>
                    <table className="w-full text-sm">
                        <thead><tr className="text-[11px] uppercase text-neutral-500"><th className="text-left pb-2">Canal</th><th className="text-right pb-2">Vis.</th><th className="text-right pb-2">Leads</th><th className="text-right pb-2">CVR</th></tr></thead>
                        <tbody>
                            {sources.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-neutral-400 text-xs">Sin datos</td></tr>}
                            {sources.map((s) => (
                                <tr key={s.channel} className="border-t border-neutral-100" data-testid={`an-channel-${s.channel}`}>
                                    <td className="py-1.5 font-medium">{CHANNEL_LABELS[s.channel] || s.channel}</td>
                                    <td className="py-1.5 text-right text-neutral-600">{s.visitas}</td>
                                    <td className="py-1.5 text-right font-semibold text-eclipse-cta">{s.leads}</td>
                                    <td className="py-1.5 text-right text-neutral-500">{s.cvr}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="bg-white border border-neutral-200 rounded-md p-5">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Top campañas (UTM)</div>
                    {camp.top_campaigns.length === 0 ? <div className="text-xs text-neutral-400 text-center py-6">Sin datos UTM</div> :
                        <ul className="space-y-2 text-sm">
                            {camp.top_campaigns.map((c, i) => (
                                <li key={i} className="flex justify-between border-b border-neutral-100 pb-1.5">
                                    <span className="text-neutral-700 truncate">{c.utm_campaign}</span>
                                    <span className="text-eclipse-cta font-semibold">{c.leads}</span>
                                </li>
                            ))}
                        </ul>
                    }
                </div>
                <div className="bg-white border border-neutral-200 rounded-md p-5">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Top keywords (UTM term)</div>
                    {camp.top_keywords.length === 0 ? <div className="text-xs text-neutral-400 text-center py-6">Sin datos</div> :
                        <ul className="space-y-2 text-sm">
                            {camp.top_keywords.map((k, i) => (
                                <li key={i} className="flex justify-between border-b border-neutral-100 pb-1.5">
                                    <span className="text-neutral-700 truncate">{k.utm_term}</span>
                                    <span className="text-eclipse-cta font-semibold">{k.leads}</span>
                                </li>
                            ))}
                        </ul>
                    }
                </div>
            </div>

            {loading && <div className="text-xs text-neutral-400 text-center">Cargando…</div>}
        </AdminLayout>
    );
}

function KPICardDelta({ label, value, delta, icon: Icon, accent = "text-neutral-900", unit = "%" }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-col gap-1">
            <div className="flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</span>
                {Icon && <Icon className={`w-3.5 h-3.5 ${accent}`} />}
            </div>
            <div className={`text-xl font-bold ${accent}`} data-testid={`an-kpi-${label.toLowerCase()}`}>{value ?? "—"}</div>
            {typeof delta === "number" && (
                <div className={`flex items-center gap-1 text-[10px] font-mono ${delta >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {delta >= 0 ? "+" : ""}{delta}{unit}
                </div>
            )}
        </div>
    );
}
